// src/services/demoStats.js
//
// The math behind the "RegX progress" section of /statistics. No Firebase here,
// so it can be tested with plain data. It only ever sees counts and the number
// fields of weekly reports, never a patient.
//
// Months are "YYYY-MM" strings and weeks are the Monday they start on, both in
// the viewer's local time. Charts stop at the last FINISHED month or week, so a
// period still in progress can't look like a drop.

import { reportPeriodCounts } from "./weeklyReportModel";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad = (n) => String(n).padStart(2, "0");

export const monthOfDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;

/** "2026-10" → the first moment of that month, local time. */
export const monthStart = (month) => {
  const [y, m] = month.split("-").map(Number);
  return new Date(y, m - 1, 1);
};

export const addMonths = (month, n) => {
  const d = monthStart(month);
  return monthOfDate(new Date(d.getFullYear(), d.getMonth() + n, 1));
};

export const lastFinishedMonth = (now = new Date()) => addMonths(monthOfDate(now), -1);

/** Every month from `from` to `to`, both included. */
export const monthsBetween = (from, to) => {
  const out = [];
  for (let m = from; m <= to; m = addMonths(m, 1)) out.push(m);
  return out;
};

/** "2026-10" → "Oct 2026". */
export const monthLabel = (month) => {
  const [y, m] = month.split("-").map(Number);
  return `${MONTHS[m - 1]} ${y}`;
};

/** "2026-10" → "Oct '26", for a chart axis. */
export const monthTick = (month) => {
  const [y, m] = month.split("-").map(Number);
  return `${MONTHS[m - 1]} '${String(y).slice(2)}`;
};

/** The Monday (local midnight) of the week holding `d`. */
export const mondayOf = (d) => {
  const day = d.getDay() || 7;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - day + 1);
};

/** Mondays of each FINISHED week, from the week holding `start` onward. */
export const finishedWeeks = (start, now = new Date()) => {
  const thisWeek = mondayOf(now).getTime();
  const out = [];
  const first = mondayOf(start);
  for (let i = 0; ; i += 1) {
    const w = new Date(first.getFullYear(), first.getMonth(), first.getDate() + i * 7);
    if (w.getTime() >= thisWeek) return out;
    out.push(w);
  }
};

/** A Monday → "Oct 5". */
export const weekLabel = (d) => `${MONTHS[d.getMonth()]} ${d.getDate()}`;

const safeNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Which month a weekly report belongs to: its own `monthId` (new reports), else
 * the month it was filed. A report with neither isn't counted.
 */
export const reportMonth = (report) => {
  if (report?.monthId) return String(report.monthId).slice(0, 7);
  const filed = report?.submittedAt?.toDate?.();
  return filed instanceof Date && !Number.isNaN(filed.getTime()) ? monthOfDate(filed) : null;
};

/** Weekly reports → { "2026-10": { screenings, preScreens } }. */
export const reportTotalsByMonth = (reports = []) => {
  const totals = {};
  reports.forEach((report) => {
    const month = reportMonth(report);
    if (!month) return;
    if (!totals[month]) totals[month] = { screenings: 0, preScreens: 0 };
    (report.studies || []).forEach((s) => {
      totals[month].screenings += safeNumber(s.screenings);
      totals[month].preScreens += safeNumber(s.preScreens);
    });
    totals[month].preScreens += safeNumber(reportPeriodCounts(report).preScreens);
  });
  return totals;
};

/** [{ key, value }] → the same points with `value` as a running total. */
export const toRunningTotal = (points) => {
  let sum = 0;
  return points.map((p) => {
    sum += p.value;
    return { ...p, value: sum };
  });
};

/** Drops the empty points before the first real one (before a workflow began). */
export const trimLeadingZeros = (points) => {
  const first = points.findIndex((p) => p.value > 0);
  return first === -1 ? [] : points.slice(first);
};

const monthPoint = (month, value) => ({ key: month, tick: monthTick(month), value });

/** { month: count } → monthly points up to the last finished month, from the first real one. */
export const monthlyPoints = (counts, now = new Date()) => {
  const end = lastFinishedMonth(now);
  const months = Object.keys(counts || {}).filter((m) => m <= end).sort();
  if (!months.length) return [];
  return trimLeadingZeros(monthsBetween(months[0], end).map((m) => monthPoint(m, counts[m] || 0)));
};

/** Pre-screens per month: the last 12 finished months, including any zero month. */
export const preScreenPoints = (totals, now = new Date()) => {
  const end = lastFinishedMonth(now);
  return monthsBetween(addMonths(end, -11), end).map((m) => monthPoint(m, totals[m]?.preScreens || 0));
};

/** Screenings: a running total from January of the last finished month's year. */
export const screeningYearPoints = (totals, now = new Date()) => {
  const end = lastFinishedMonth(now);
  const from = `${end.slice(0, 4)}-01`;
  return toRunningTotal(monthsBetween(from, end).map((m) => monthPoint(m, totals[m]?.screenings || 0)));
};

/** The last 3 finished months against the same 3 months a year earlier. */
export const compareToLastYear = (counts, now = new Date()) => {
  const end = lastFinishedMonth(now);
  const months = monthsBetween(addMonths(end, -2), end);
  const sum = (list) => list.reduce((total, m) => total + (counts?.[m] || 0), 0);
  const recent = sum(months);
  const prior = sum(months.map((m) => addMonths(m, -12)));
  const range = (a, b) => `${monthLabel(a).slice(0, 3)}–${monthLabel(b)}`;
  return {
    recent,
    prior,
    recentRange: range(months[0], months[2]),
    priorRange: range(addMonths(months[0], -12), addMonths(months[2], -12)),
    percent: prior > 0 ? Math.round(((recent - prior) / prior) * 100) : null,
  };
};

/** The first month that has any, and the last finished month. */
export const firstAndLastMonth = (counts, now = new Date()) => {
  const points = monthlyPoints(counts, now);
  if (!points.length) return null;
  const first = points[0];
  const last = points[points.length - 1];
  return {
    firstMonth: first.key,
    firstValue: first.value,
    lastMonth: last.key,
    lastValue: last.value,
  };
};

export const formatCount = (n) => Number(n || 0).toLocaleString("en-US");
